import { useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Building2, MapPin, ParkingCircle, DoorOpen, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { api } from '@/api/client';
import { searchAddress, type GeocodingResult } from '@/api/geo';
import { getClientLocations, type ClientLocation } from '@/api/client-locations';
import type { Waypoint, WaypointType } from '../lib/types';
import { cn } from '@/lib/utils';

interface WaypointSearchProps {
  onAddWaypoint: (waypoint: Omit<Waypoint, 'id'>) => void;
}

interface ClientSearchResult {
  id: number;
  name: string;
  address?: string;
}

interface ClientsResponse {
  data: ClientSearchResult[];
  total: number;
}

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function WaypointSearch({ onAddWaypoint }: WaypointSearchProps) {
  const [query, setQuery] = useState('');
  const [expandedClientId, setExpandedClientId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(query, 300);

  // Search clients
  const {
    data: clientsResponse,
    isLoading: clientsLoading,
  } = useQuery({
    queryKey: ['route-planner-clients', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 2) return null;
      const response = await api.get<ClientsResponse>('/clients', {
        params: { search: debouncedQuery, limit: 5 },
      });
      return response.data;
    },
    enabled: debouncedQuery.length >= 2,
    staleTime: 30000,
  });

  // Search addresses via geocoding
  const {
    data: addressResults,
    isLoading: addressLoading,
  } = useQuery({
    queryKey: ['route-planner-geocode', debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 3) return null;
      const result = await searchAddress(debouncedQuery, { limit: 3 });
      return result.features;
    },
    enabled: debouncedQuery.length >= 3,
    staleTime: 60000,
  });

  // Get locations for expanded client
  const {
    data: clientLocations,
    isLoading: locationsLoading,
  } = useQuery({
    queryKey: ['route-planner-locations', expandedClientId],
    queryFn: async () => {
      if (!expandedClientId) return null;
      return getClientLocations(expandedClientId);
    },
    enabled: !!expandedClientId,
    staleTime: 60000,
  });

  const handleClientClick = useCallback((clientId: number) => {
    setExpandedClientId((prev) => (prev === clientId ? null : clientId));
  }, []);

  const handleLocationClick = useCallback(
    (client: ClientSearchResult, location: ClientLocation) => {
      const type: WaypointType = location.type === 'PARKING' ? 'P' : 'E';
      const officeSuffix = location.office ? ` (${location.office.name})` : '';

      onAddWaypoint({
        lat: location.latitude,
        lon: location.longitude,
        label: `${client.name}${officeSuffix} - ${location.name}`,
        type,
        clientId: client.id,
        officeId: location.officeId,
      });

      // Clear search
      setQuery('');
      setExpandedClientId(null);
    },
    [onAddWaypoint]
  );

  const handleAddressClick = useCallback(
    (result: GeocodingResult) => {
      const [lon, lat] = result.geometry.coordinates;
      const label = result.properties.display_name || result.properties.name || 'Адрес';

      onAddWaypoint({
        lat,
        lon,
        label: label.split(',').slice(0, 2).join(','), // Shorten to first 2 parts
        type: 'A',
      });

      // Clear search
      setQuery('');
    },
    [onAddWaypoint]
  );

  const isLoading = clientsLoading || addressLoading;
  const hasQuery = debouncedQuery.length >= 2;
  const clients = clientsResponse?.data || [];
  const addresses = addressResults || [];
  const hasResults = clients.length > 0 || addresses.length > 0;

  return (
    <div className="space-y-3">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Клиент или адрес..."
          className="pl-9 pr-9"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Results */}
      {hasQuery && !isLoading && !hasResults && (
        <div className="text-sm text-muted-foreground text-center py-4">
          Ничего не найдено
        </div>
      )}

      {hasQuery && hasResults && (
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {/* Clients */}
          {clients.map((client) => (
            <div key={client.id} className="space-y-1">
              <button
                onClick={() => handleClientClick(client.id)}
                className={cn(
                  'w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors',
                  expandedClientId === client.id
                    ? 'bg-primary/10'
                    : 'hover:bg-muted'
                )}
              >
                <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{client.name}</div>
                  {client.address && (
                    <div className="text-xs text-muted-foreground truncate">
                      {client.address}
                    </div>
                  )}
                </div>
              </button>

              {/* Client locations */}
              {expandedClientId === client.id && (
                <div className="ml-6 space-y-1">
                  {locationsLoading && (
                    <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Загрузка...
                    </div>
                  )}
                  {clientLocations && clientLocations.length === 0 && (
                    <div className="p-2 text-sm text-muted-foreground">
                      Нет точек у клиента
                    </div>
                  )}
                  {clientLocations?.map((location) => (
                    <button
                      key={location.id}
                      onClick={() => handleLocationClick(client, location)}
                      className="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-muted transition-colors"
                    >
                      {location.type === 'PARKING' ? (
                        <ParkingCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      ) : (
                        <DoorOpen className="w-4 h-4 text-green-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{location.name}</div>
                        {location.office && (
                          <div className="text-xs text-muted-foreground truncate">
                            {location.office.name}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Divider if both clients and addresses */}
          {clients.length > 0 && addresses.length > 0 && (
            <div className="border-t my-2" />
          )}

          {/* Addresses */}
          {addresses.map((result, index) => (
            <button
              key={`addr-${index}`}
              onClick={() => handleAddressClick(result)}
              className="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-muted transition-colors"
            >
              <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <div className="text-sm truncate">
                {result.properties.display_name || result.properties.name}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
